// The "just send a number" answer for a signal template (Phase 3 of
// docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md - a Templates-screen
// "Copy integration snippet" action): a non-technical sender (an Excel
// macro, a cron'd curl, a homegrown script) can hardcode this signal's own
// `number` and POST straight to /signal - no Mapping/`rules` authoring
// needed at all, since the server resolves signalNumber into this exact
// preset's type/value server-side (modules/signal/transitions/signal.go's
// ResolveSignalNumber, unchanged by anything Phase 0-2 added).
//
// `date` is included deliberately, not left to the sender to add later -
// signal/create.wsl's StoreDate reads $json.date with no `??` fallback, so
// a request that omits the key entirely fails action-argument binding (see
// [[wsl-qs-missing-param-binding-bug]]) rather than just leaving the field
// blank. Every snippet ships a real, replaceable ISO 8601 timestamp so a
// naive copy-paste actually works.
export interface SignalSnippetInput {
  number: string;
  channelId?: string;
  value?: string;
  at?: Date;
}

export interface SignalSnippet {
  body: Record<string, string>;
  json: string;
  curl: string;
  vba: string;
}

const PLACEHOLDER_CHANNEL = '@your-channel';
const PLACEHOLDER_TOKEN = '<your-token>';
const PLACEHOLDER_HOST = 'https://<your-zmist-host>';

export function buildSignalSnippet({ number, channelId, value, at }: SignalSnippetInput): SignalSnippet {
  const body = {
    channelId: channelId || PLACEHOLDER_CHANNEL,
    signalNumber: number,
    value: value ?? '',
    date: (at ?? new Date()).toISOString(),
  };
  const bodyJson = JSON.stringify(body);

  const json = JSON.stringify(body, null, 2);

  const curl = [
    `curl -X POST '${PLACEHOLDER_HOST}/signal' \\`,
    `  -H 'Authorization: Bearer ${PLACEHOLDER_TOKEN}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '${bodyJson}'`,
  ].join('\n');

  // Excel/VBA's own quoting: a VBA string literal escapes an embedded "
  // as "" - the JSON body is otherwise passed through verbatim.
  const vbaBody = bodyJson.replace(/"/g, '""');
  const vba = [
    'Dim http As Object',
    'Set http = CreateObject("MSXML2.XMLHTTP")',
    `http.Open "POST", "${PLACEHOLDER_HOST}/signal", False`,
    `http.setRequestHeader "Authorization", "Bearer ${PLACEHOLDER_TOKEN}"`,
    'http.setRequestHeader "Content-Type", "application/json"',
    `http.send "${vbaBody}"`,
    'MsgBox http.responseText',
  ].join('\n');

  return { body, json, curl, vba };
}
